import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $notificationStore } from '../notificationStore';

import { AccountsApi, CardsApi, UsersApi } from '@shared/api/rest';
import type {
  AddUserAccountsRequest,
  AddUserCardsRequest,
  CreateUserRequest,
  DeleteUserAccountsRequest,
  DeleteUserCardsRequest,
  UsersRequest,
  UsersResponse,
  UserWithPaymentInfo,
} from '@shared/api/rest/users';
import { Frequency, type CardLimitsResponse } from '@shared/api/rest/cards';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';
import { ApiUtils } from '@shared/utils';

const getLimit = (limit: CardLimitsResponse | undefined): number => {
  if (!limit) return 0;

  let amount = 0;
  if (limit.frequency === Frequency.DAILY) {
    amount = limit.dayLimits?.otcAmount || 0;
  } else if (limit.frequency === Frequency.CYCLE) {
    amount = limit.cycleLimits?.otcAmount || 0;
  } else if (limit.frequency === Frequency.YEAR) {
    amount = limit.yearLimits?.otcAmount || 0;
  }
  return limit.currentOTCAmount - amount;
};

const getUsersWithoutPermissionEffect = createEffect(async (params: IQueryParams<UsersRequest>) => {
  const data = await UsersApi.getUsers(params.request, params?.signal);
  if (!data.success) throw new Error(data.message || 'Error add user');

  return {
    total: data.total,
    data: data.data?.map((user) => ({ ...user, accounts: [], cards: [] })) || [],
  };
});
const getUsersEffect = createEffect((params: IQueryParams<UsersRequest>) => UsersApi.getUsers(params.request, params?.signal));
const getPaymentInfosEffect = createEffect(async (data: { users: UsersResponse[] | undefined; total: number }) => {
  const users = data.users as UsersResponse[];
  const usersWithPaymentInfo = await Promise.all(
    users.map(async (user) => {
      const [cardsData, accountsData] = await Promise.all([
        CardsApi.getCards({ AssignedUserId: user.id }),
        AccountsApi.getAccounts({ AssignedUserId: user.id }),
      ]);
      if (!cardsData.success) throw new Error(cardsData.message || 'Error get cards');
      if (!accountsData.success) throw new Error(accountsData.message || 'Error get accounts');

      return {
        ...user,
        cards: cardsData.data || [],
        accounts: accountsData.data || [],
      };
    }),
  );

  return {
    total: data.total,
    data: usersWithPaymentInfo,
  };
});
const addUserEffect = createEffect(async (request: { user: CreateUserRequest; params: UsersRequest }) => {
  const user = await UsersApi.addUser(request.user);
  if (!user.success) throw new Error(user.message || 'Error add user');

  return {
    params: request.params,
    // TODO: Remove UserType after fixing on the backend of the response
    user: { ...user.data, userType: request.user.userType } as UsersResponse,
  };
});
const getPaymentInfoEffect = createEffect(async (data: { user: UsersResponse | undefined; params: UsersRequest }) => {
  const user = data.user as UsersResponse;
  const [cardsData, accountsData] = await Promise.all([
    CardsApi.getCards({ AssignedUserId: user.id }),
    AccountsApi.getAccounts({ AssignedUserId: user.id }),
  ]);
  if (!cardsData.success) throw new Error(cardsData.message || 'Error get cards');
  if (!accountsData.success) throw new Error(accountsData.message || 'Error get accounts');

  return {
    ...user,
    cards: cardsData.data || [],
    accounts: accountsData.data || [],
  };
});
const getBalancesEffect = createEffect(async (request: { cardIds: number[]; userId: string }) =>
  Promise.all(request.cardIds.map((cardId) => CardsApi.getLimits(cardId))),
);
const addAccountEffect = createEffect(async (request: AddUserAccountsRequest) => {
  const user = await UsersApi.addUserAccounts(request);
  if (!user.success) throw new Error(user.message || 'Error add account');

  return {
    ...request,
    user: user.data,
  };
});
const getAccountInfoEffect = createEffect(async (request: AddUserAccountsRequest) => {
  // TODO: Remove delay after fixing on backend asynchronous account addition
  const delay = new Promise((res) => {
    setTimeout(() => {
      res('');
    }, 3000);
  });
  await delay;
  return AccountsApi.getAccounts({ AssignedUserId: request.userIds });
});
const addCardEffect = createEffect(async (request: AddUserCardsRequest) => {
  const user = await UsersApi.addUserCards(request);
  if (!user.success) throw new Error(user.message || 'Error add account');

  return {
    ...request,
    user: user.data,
  };
});
const getCardInfoEffect = createEffect(async (request: AddUserCardsRequest) => {
  // TODO: Remove delay after fixing on backend asynchronous card addition
  const delay = new Promise((res) => {
    setTimeout(() => {
      res('');
    }, 3000);
  });
  await delay;
  const card = await CardsApi.getCards({ AssignedUserId: request.userIds });
  return {
    ...request,
    data: card.data,
  };
});
const removeAccountEffect = createEffect(async (params: IQueryParams<DeleteUserAccountsRequest>) =>
  UsersApi.deleteUserAccounts(params.request, params?.signal),
);
const removeCardEffect = createEffect((params: IQueryParams<DeleteUserCardsRequest>) => UsersApi.deleteUserCards(params.request, params?.signal));
const clearEvent = createEvent();
sample({
  source: getUsersEffect.doneData,
  fn: (data) => ({
    total: data.total,
    users: data.data,
  }),
  filter: (data) => data.data?.length !== 0,
  target: getPaymentInfosEffect,
});
sample({
  source: addUserEffect.doneData,
  target: getPaymentInfoEffect,
});
sample({
  source: addAccountEffect.doneData,
  target: getAccountInfoEffect,
});
sample({
  source: addCardEffect.doneData,
  target: getCardInfoEffect,
});
sample({
  source: getCardInfoEffect.doneData,
  fn: (data) => {
    return {
      cardIds: data.data?.map((card) => card.cardId) || [],
      userId: data.userIds,
    };
  },
  target: getBalancesEffect,
});

const $dataStore = createStore<{ total: number; data: UserWithPaymentInfo[] }>({
  total: 0,
  data: [],
})
  .on(getUsersWithoutPermissionEffect.done, (current, data) => (data.result ? { total: data.result.total, data: data.result.data || [] } : current))
  .on(getPaymentInfosEffect.done, (current, data) => (data.result ? { total: data.result.total, data: data.result.data || [] } : current))
  .on(getPaymentInfoEffect.done, (current, data) => ({
    ...current,
    data: current.data.length >= data.params.params.Count! ? current.data : [...current.data, data.result],
  }))
  .on(getBalancesEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userId) {
        return {
          ...user,
          cards: user.cards.map((card, idx) => ({
            ...card,
            balance: getLimit(data.result[idx].data),
          })),
        };
      }
      return user;
    }),
  }))

  .on(getAccountInfoEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userIds) {
        return {
          ...user,
          accounts: data.result.data || [],
        };
      }
      return user;
    }),
  }))
  .on(getCardInfoEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === data.params.userIds) {
        return {
          ...user,
          cards: data.result.data || [],
        };
      }
      return user;
    }),
  }))
  .on(removeAccountEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === (data.params.request as DeleteUserAccountsRequest).userIds) {
        return {
          ...user,
          accounts: user.accounts.filter((account) => account.id !== (data.params.request as DeleteUserAccountsRequest).accountIds[0]),
        };
      }
      return user;
    }),
  }))
  .on(removeCardEffect.done, (current, data) => ({
    ...current,
    data: current.data.map((user) => {
      if (user.id === (data.params.request as DeleteUserCardsRequest).userIds) {
        return {
          ...user,
          cards: user.cards.filter((card) => card.cardId !== (data.params.request as DeleteUserCardsRequest).cardIds[0]),
        };
      }
      return user;
    }),
  }))
  .reset(clearEvent);

const $initialLoadingStore = createStore<boolean>(false)
  .on(getUsersEffect.pending, (_, data) => data)
  .on(getUsersEffect.fail, (_, data) => ApiUtils.abortApiFailCaseTrigger(data))
  .on(getPaymentInfosEffect.pending, (_, data) => data)
  .on(getPaymentInfosEffect.fail, (_, data) => false)
  .on(getPaymentInfosEffect.done, (_, data) => false)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(addUserEffect.pending, (_, data) => data)
  .on(addUserEffect.fail, (_, data) => false)
  .on(addUserEffect.done, (_, data) => false)
  .on(getPaymentInfoEffect.pending, (_, data) => data)
  .on(getPaymentInfoEffect.fail, (_, data) => false)
  .on(getPaymentInfoEffect.done, (_, data) => false)
  .on(getAccountInfoEffect.pending, (_, data) => data)
  .on(getAccountInfoEffect.fail, (_, data) => false)
  .on(getAccountInfoEffect.done, (_, data) => false)
  .on(getCardInfoEffect.pending, (_, data) => data)
  .on(getCardInfoEffect.fail, (_, data) => false)
  .on(getCardInfoEffect.done, (_, data) => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUsersWithoutPermissionEffect, () => null)
  .on(getUsersWithoutPermissionEffect.fail, (_, data) => data.error.message)
  .on(getUsersWithoutPermissionEffect.done, (_, data) => null)
  .on(getUsersEffect, () => null)
  .on(getUsersEffect.fail, (_, data) => data.error.message)
  .on(getUsersEffect.done, (_, data) => null)
  .on(getPaymentInfosEffect, () => null)
  .on(getPaymentInfosEffect.fail, (_, data) => data.error.message)
  .on(getPaymentInfosEffect.done, (_, data) => null)
  .on(addUserEffect, () => null)
  .on(addUserEffect.fail, (_, data) => data.error.message)
  .on(addUserEffect.done, (_, data) => null)
  .on(getPaymentInfoEffect, () => null)
  .on(getPaymentInfoEffect.fail, (_, data) => data.error.message)
  .on(getPaymentInfoEffect.done, (_, data) => null)
  .on(getBalancesEffect, () => null)
  .on(getBalancesEffect.fail, (_, data) => data.error.message)
  .on(getBalancesEffect.done, (_, data) => null)
  .on(addAccountEffect, () => null)
  .on(addAccountEffect.fail, (_, data) => data.error.message)
  .on(addAccountEffect.done, (_, data) => null)
  .on(getAccountInfoEffect, () => null)
  .on(getAccountInfoEffect.fail, (_, data) => data.error.message)
  .on(getAccountInfoEffect.done, (_, data) => null)
  .on(addCardEffect, () => null)
  .on(addCardEffect.fail, (_, data) => data.error.message)
  .on(addCardEffect.done, (_, data) => null)
  .on(getCardInfoEffect, () => null)
  .on(getCardInfoEffect.fail, (_, data) => data.error.message)
  .on(getCardInfoEffect.done, (_, data) => null)
  .on(removeAccountEffect, () => null)
  .on(removeAccountEffect.fail, (_, data) => data.error.message)
  .on(removeAccountEffect.done, (_, data) => null)
  .on(removeCardEffect, () => null)
  .on(removeCardEffect.fail, (_, data) => data.error.message)
  .on(removeCardEffect.done, (_, data) => null)
  .reset(clearEvent);
$errorStore.watch((x) => {
  ApiUtils.errorStoreAbortRequestWatcher(x, $notificationStore.events.setError);
});

export const $usersStore = {
  events: {
    getUsersWithoutPermission: ApiUtils.wrapperApi(getUsersWithoutPermissionEffect, RequestStrategyEnum.Debounce),
    getUsers: ApiUtils.wrapperApi(getUsersEffect, RequestStrategyEnum.Debounce),
    addUser: addUserEffect,
    getBalances: getBalancesEffect,
    addAccount: addAccountEffect,
    addCard: addCardEffect,
    removeAccount: ApiUtils.wrapperApi(removeAccountEffect, RequestStrategyEnum.Debounce),
    removeCard: ApiUtils.wrapperApi(removeCardEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    initialLoading: $initialLoadingStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
